import { Injectable } from '@angular/core';

interface CacheRecord<T> {
  key: string;
  value: T;
  savedAt: number;
}

@Injectable({ providedIn: 'root' })
export class IndexedDbCacheService {
  private readonly databaseName = 'day-pass-cache';
  private readonly storeName = 'responses';
  private databasePromise?: Promise<IDBDatabase>;
  private invalidatedAt = 0;

  async get<T>(key: string): Promise<{ value: T; savedAt: number } | null> {
    let database: IDBDatabase;
    try {
      database = await this.open();
    } catch {
      return null;
    }
    return new Promise((resolve) => {
      const request = database
        .transaction(this.storeName, 'readonly')
        .objectStore(this.storeName)
        .get(key);
      request.onsuccess = () => {
        const record = request.result as CacheRecord<T> | undefined;
        resolve(
          record && record.savedAt > this.invalidatedAt
            ? { value: record.value, savedAt: record.savedAt }
            : null,
        );
      };
      request.onerror = () => resolve(null);
    });
  }

  async set<T>(key: string, value: T): Promise<void> {
    let database: IDBDatabase;
    try {
      database = await this.open();
    } catch {
      return;
    }
    await new Promise<void>((resolve) => {
      const request = database
        .transaction(this.storeName, 'readwrite')
        .objectStore(this.storeName)
        .put({ key, value, savedAt: Date.now() } satisfies CacheRecord<T>);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
  }

  async clear(): Promise<void> {
    this.invalidatedAt = Date.now();
    let database: IDBDatabase;
    try {
      database = await this.open();
    } catch {
      return;
    }
    await new Promise<void>((resolve) => {
      const request = database
        .transaction(this.storeName, 'readwrite')
        .objectStore(this.storeName)
        .clear();
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
  }

  private open(): Promise<IDBDatabase> {
    if (this.databasePromise) return this.databasePromise;
    if (typeof indexedDB === 'undefined')
      return Promise.reject(new Error('IndexedDB is not available.'));
    this.databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.databaseName, 1);
      request.onupgradeneeded = () =>
        request.result.createObjectStore(this.storeName, { keyPath: 'key' });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Unable to open cache.'));
    });
    return this.databasePromise;
  }
}
